import React, { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Layout, Flex, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import useUser from 'utils/useUser';
import {
  hasInteractiveTimeDashboardAccess,
  hasCcmDashboardAccess,
  hasHomeDashboardAccess,
} from 'utils/userTypeHelper';
import DashboardTabs from 'components/molecules/oneplaform/dashboard-tabs';
import RpmDashboardHeaderActions from 'components/molecules/rpm-dashboard-header-actions';
import { AccountsSelect } from 'components/organisms/ccm-console/select-boxes/select-account';
import { Routes } from 'router/routes';

import styles from './styles.module.scss';

import type { DashboardTabValue } from 'components/molecules/oneplaform/dashboard-tabs/constants';
import type { RangePickerValue } from 'components/atoms/base-range-datepicker';
import type { TOverviewDashboardFilter } from 'infrastructure/hooks/rpm/overview-dashboard';
import type { TCCMDataFilters } from 'infrastructure/hooks/ccm/console/use-ccm-data-query-filter';

const { Header } = Layout;
const { Text } = Typography;

interface DashboardLayoutProps {
  children: React.ReactNode;
  title?: string;
  dateValues?: RangePickerValue;
  onDateChange?: (
    dates: [Date | null, Date | null],
    dateString: [string, string],
    info: { range?: 'start' | 'end' },
    presetKey?: string,
  ) => void;
  filterValues?: {
    patientGuid?: string;
    doctorGuid?: string | string[];
    clinicGuid?: string;
  };
  onFilterChange?: (key: string, value: any) => void;
  onPatientLoaded?: (patientName: string | null) => void;
  onClinicLoaded?: (clinicName: string | null) => void;
  onAgencyDoctorLoaded?: (agencyDoctorName: string | null) => void;
  filterCount?: number;
  showRpmHeaderActions?: boolean;
  filter?: TOverviewDashboardFilter;
  updateFilter?: (newFilter: Partial<TOverviewDashboardFilter>) => void;
  resetFilter?: () => void;

  ccmFilter?: TCCMDataFilters;
  updateCcmFilter?: (newFilter: Partial<TCCMDataFilters>) => void;
  showCcmHeaderActions?: boolean;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  children,
  title,
  dateValues,
  onDateChange,
  filterValues,
  onFilterChange,
  onPatientLoaded,
  onClinicLoaded,
  onAgencyDoctorLoaded,
  filterCount,
  showRpmHeaderActions = false,
  filter,
  updateFilter,
  resetFilter,
  ccmFilter,
  updateCcmFilter,
  showCcmHeaderActions = false,
}) => {
  const user = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const activeTab = useMemo<DashboardTabValue>(() => {
    if (location.pathname === Routes.homeDashboard) return 'home';
    if (location.pathname === Routes.dashboard) return 'rpm';
    if (location.pathname === Routes.ccm) return 'ccm';
    return 'home';
  }, [location.pathname]);

  const hasHomeAccess = useMemo(
    () => (user?.userType ? hasHomeDashboardAccess(user.userType) : false),
    [user?.userType],
  );

  const hasRPMAccess = useMemo(
    () =>
      user?.userType && user?.roleNames
        ? hasInteractiveTimeDashboardAccess(user.userType, user.roleNames)
        : false,
    [user?.userType, user?.roleNames],
  );

  const hasCCMAccess = useMemo(
    () =>
      user?.userType && user?.roleNames
        ? hasCcmDashboardAccess(user.userType, user.roleNames)
        : false,
    [user?.userType, user?.roleNames],
  );

  const handleTabChange = useCallback(
    (value: DashboardTabValue) => {
      if (value === 'home') {
        navigate(Routes.homeDashboard);
      } else if (value === 'rpm') {
        navigate(Routes.dashboard);
      } else if (value === 'ccm') {
        navigate(Routes.ccm);
      }
    },
    [navigate],
  );

  const pageTitle = useMemo(() => {
    if (title) return title;
    if (activeTab === 'rpm') return t('links.rpmDashboard', 'RPM Dashboard');
    if (activeTab === 'ccm') return t('links.ccmDashboard', 'CCM Dashboard');
    return t('homeDashboard.title', 'Home Dashboard');
  }, [title, activeTab, t]);

  return (
    <Layout className={styles.layout}>
      {hasHomeAccess && (
        <Header className={styles['breadcrumb-bar']}>
          <Flex
            align="center"
            gap={16}
            className={styles['breadcrumb-bar-inner']}
            justify="space-between"
          >
            <Flex align="center" gap={16}>
              <Text className={styles.pageTitle}>{pageTitle}</Text>
              <DashboardTabs
                activeTab={activeTab}
                onTabChange={handleTabChange}
                hasHomeAccess={hasHomeAccess}
                hasRPMAccess={hasRPMAccess}
                hasCCMAccess={hasCCMAccess}
              />
            </Flex>
            {activeTab === 'rpm' && showRpmHeaderActions && (
              <RpmDashboardHeaderActions
                filterValues={filterValues}
                onFilterChange={onFilterChange}
                onPatientLoaded={onPatientLoaded}
                onClinicLoaded={onClinicLoaded}
                onAgencyDoctorLoaded={onAgencyDoctorLoaded}
                dateValues={dateValues}
                onDateChange={onDateChange}
                filterCount={filterCount}
                filter={filter}
                updateFilter={updateFilter}
                resetFilter={resetFilter}
              />
            )}
            {activeTab === 'ccm' &&
              showCcmHeaderActions &&
              ccmFilter &&
              updateCcmFilter && (
                <AccountsSelect
                  clinicGuid={ccmFilter['f-clinicGuid']}
                  updateFilter={updateCcmFilter}
                />
              )}
          </Flex>
        </Header>
      )}
      <div
        className={`${styles.content} ${
          activeTab !== 'home' ? styles['content-with-margin'] : ''
        }`}
      >
        {children}
      </div>
    </Layout>
  );
};

export default DashboardLayout;
